import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, updatePassword } from 'firebase/auth';
import { getDB, getAuthInstance } from '../config/firebase';
import { User, UserFormData } from '../types';

const USERS_COLLECTION = 'users';

// Helper function to safely convert Firestore timestamps to dates
const safeToDate = (timestamp: any): Date | undefined => {
  if (!timestamp) return undefined;
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  return undefined;
};

export class UserService {
  // Get all users with pagination
  static async getUsers(pageSize: number = 20, lastDoc?: QueryDocumentSnapshot<DocumentData>) {
    try {
      let q = query(
        collection(getDB(), USERS_COLLECTION),
        limit(pageSize)
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const users: User[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        users.push({
          id: doc.id,
          ...data,
          createdAt: safeToDate(data.createdAt) || new Date(),
          lastLogin: safeToDate(data.lastLogin)
        } as User);
      });

      return {
        users,
        lastDoc: snapshot.docs[snapshot.docs.length - 1],
        hasMore: snapshot.docs.length === pageSize
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      throw new Error('Failed to fetch users');
    }
  }

  // Get user by ID
  static async getUserById(userId: string): Promise<User | null> {
    try {
      const docRef = doc(getDB(), USERS_COLLECTION, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: safeToDate(data.createdAt) || new Date(),
        lastLogin: safeToDate(data.lastLogin)
      } as User;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw new Error('Failed to fetch user');
    }
  }

  // Create new user
  static async createUser(userData: UserFormData): Promise<User> {
    try {
      // Create user in Firebase Auth if email is provided
      let authUserId = '';
      if (userData.email && userData.password) {
        const userCredential = await createUserWithEmailAndPassword(
          getAuthInstance(),
          userData.email,
          userData.password
        );
        authUserId = userCredential.user.uid;
      }

      // Create user document in Firestore
      const userDoc = {
        username: userData.username,
        role: userData.role as 'admin' | 'faculty' | 'staff',
        email: userData.email || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        isActive: true,
        createdAt: new Date(),
        authUserId: authUserId
      };

      const docRef = await addDoc(collection(getDB(), USERS_COLLECTION), userDoc);
      
      return {
        id: docRef.id,
        ...userDoc
      } as User;
    } catch (error: any) {
      console.error('Error creating user:', error);
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('Email is already in use');
      }
      throw new Error('Failed to create user');
    }
  }

  // Update user
  static async updateUser(userId: string, updates: Partial<User>): Promise<void> {
    try {
      const docRef = doc(getDB(), USERS_COLLECTION, userId);
      const updateData = {
        ...updates,
        updatedAt: new Date()
      };
      
      // Remove undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key as keyof typeof updateData] === undefined) {
          delete updateData[key as keyof typeof updateData];
        }
      });

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating user:', error);
      throw new Error('Failed to update user');
    }
  }

  // Delete user
  static async deleteUser(userId: string): Promise<void> {
    try {
      const docRef = doc(getDB(), USERS_COLLECTION, userId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw new Error('Failed to delete user');
    }
  }

  // Search users (simplified to avoid composite index requirements)
  static async searchUsers(searchTerm: string): Promise<User[]> {
    try {
      // Simple query without orderBy to avoid composite index
      const q = query(
        collection(getDB(), USERS_COLLECTION),
        where('isActive', '==', true)
      );

      const snapshot = await getDocs(q);
      const users: User[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        const user = {
          id: doc.id,
          ...data,
          createdAt: safeToDate(data.createdAt) || new Date(),
          lastLogin: safeToDate(data.lastLogin)
        } as User;

        // Client-side search filtering
        const searchLower = searchTerm.toLowerCase();
        if (
          user.firstName?.toLowerCase().includes(searchLower) ||
          user.lastName?.toLowerCase().includes(searchLower) ||
          user.username.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower)
        ) {
          users.push(user);
        }
      });

      // Sort client-side
      users.sort((a, b) => (a.firstName || '').localeCompare(b.firstName || ''));
      return users;
    } catch (error) {
      console.error('Error searching users:', error);
      throw new Error('Failed to search users');
    }
  }

  // Get users by role (simplified to avoid composite index requirements)
  static async getUsersByRole(role: string): Promise<User[]> {
    try {
      // Simple query without orderBy to avoid composite index
      const q = query(
        collection(getDB(), USERS_COLLECTION),
        where('role', '==', role),
        where('isActive', '==', true)
      );

      const snapshot = await getDocs(q);
      const users: User[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        users.push({
          id: doc.id,
          ...data,
          createdAt: safeToDate(data.createdAt) || new Date(),
          lastLogin: safeToDate(data.lastLogin)
        } as User);
      });

      // Sort client-side
      users.sort((a, b) => (a.firstName || '').localeCompare(b.firstName || ''));
      return users;
    } catch (error) {
      console.error('Error fetching users by role:', error);
      throw new Error('Failed to fetch users by role');
    }
  }

  // Get total user count
  static async getTotalUserCount(): Promise<number> {
    try {
      const snapshot = await getDocs(collection(getDB(), USERS_COLLECTION));
      return snapshot.size;
    } catch (error) {
      console.error('Error getting user count:', error);
      throw new Error('Failed to get user count');
    }
  }

  // Toggle user active status
  static async toggleUserStatus(userId: string): Promise<void> {
    try {
      const user = await this.getUserById(userId);
      if (!user) {
        throw new Error('User not found');
      }

      const docRef = doc(getDB(), USERS_COLLECTION, userId);
      await updateDoc(docRef, {
        isActive: !user.isActive,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error toggling user status:', error);
      throw new Error('Failed to toggle user status');
    }
  }
} 
